/**
 * Logsheet Checker (Phase 13 Plan 13-02) — frontend wire DTOs mirroring the backend
 * contract delivered by Plan 13-01.
 *
 * Property names use camelCase exactly as the backend serializes via the Phase 1
 * JsonNamingPolicy (camelCase). RejectionReason is a string union matching the
 * JsonStringEnumConverter output (PascalCase member names per D-118).
 */

import type { LogsheetDocument, LogsheetStatus } from '@/types/logsheets-maker';
import type { PaginatedResult } from '@/types/roles';

/** Re-export for convenience */
export type { LogsheetDocument, LogsheetStatus, PaginatedResult };

/**
 * Rejection reason codes — mirrors `RejectionReasonDto` enum (Plan 13-01).
 * The backend emits these as PascalCase strings via JsonStringEnumConverter.
 */
export type RejectionReason =
  | 'IncorrectKpi'
  | 'MissingDoc'
  | 'WrongMachine'
  | 'DateMismatch'
  | 'Other';

/** Human-readable labels for each rejection reason (used in Select options). */
export const REJECTION_REASON_LABELS: Readonly<Record<RejectionReason, string>> = {
  IncorrectKpi: 'Incorrect KPI values',
  MissingDoc: 'Missing document',
  WrongMachine: 'Wrong machine',
  DateMismatch: 'Date mismatch',
  Other: 'Other',
};

/** A row in `GET /api/logsheets/checker/pending` (paginated list). */
export interface PendingItem {
  readonly id: number;
  readonly entryDate: string;
  readonly siteId: number;
  readonly siteName: string;
  readonly machineId: number;
  readonly machineCode: string;
  readonly machineName: string;
  /**
   * Denormalized "Vehicle Number" custom field (key `vehicle_number`), surfaced next to the
   * machine name via {@link formatMachineName}. `null` when the machine has no vehicle number
   * recorded — the machine code is the display fallback. Inherited by CheckerDetail + HistoryItem.
   */
  readonly vehicleNumber: string | null;
  readonly makerUserId: number;
  readonly makerName: string;
  readonly type: 'Work' | 'Idle';
  readonly kpisCount: number;
  readonly submittedAt: string;
  readonly daysPending: number;
  /**
   * Phase 14 Plan 14-02 (D-128): True when this entry was rejected back to Checker by an Approver
   * (RouteTo=Checker path). The Checker Queue renders a "From Approver" badge on these rows.
   * Defaults to false for all pre-Phase-14 entries.
   */
  readonly routedToCheckerByApprover: boolean;
  /**
   * Phase 15 Plan 15-02 (D-138): True when the (machine, month) tuple for this entry has been
   * locked by a monthly-document upload. Optional because the backend list projection (PendingItemDto)
   * does not yet include this field — the backend 15-01 plan only added mutation guards, not list
   * projection extensions. When the field is absent (undefined), the badge is not shown.
   * Future backend addendum: add `MonthLocked bool` to `PendingItemDto` and service projection.
   */
  readonly monthLocked?: boolean;
}

/** Full detail from `GET /api/logsheets/checker/pending/:id`. */
export interface CheckerDetail extends PendingItem {
  readonly machineTypeId: number;
  readonly machineTypeName: string;
  readonly shift: string | null;
  readonly startTime: string;
  readonly endTime: string | null;
  readonly workDescription: string | null;
  readonly idleReason: string | null;
  readonly notes: string | null;
  readonly kpiValues: Readonly<Record<string, string | number | boolean | null>>;
  readonly status: LogsheetStatus;
  readonly checkedByUserId: number | null;
  readonly checkedByUserName: string | null;
  readonly checkedAt: string | null;
  readonly approvedByUserId: number | null;
  readonly approvedByUserName: string | null;
  readonly approvedAt: string | null;
  readonly rejectionReason: string | null;
  readonly rejectedByUserId: number | null;
  readonly rejectedByUserName: string | null;
  readonly rejectedAt: string | null;
  readonly rejectionReasonCode: number | null;
  readonly documents: readonly LogsheetDocument[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Last-5 comparison entry from `GET /api/logsheets/checker/pending/:id/recent-entries`. */
export interface RecentEntry {
  readonly id: number;
  readonly entryDate: string;
  readonly status: string;
  readonly kpiValuesJson: string;
  readonly checkedByUserId: number | null;
  readonly checkedAt: string | null;
}

/** Request body for `POST /api/logsheets/checker/pending/:id/approve`. */
export interface ApproveRequest {
  readonly remarks?: string;
}

/** Request body for `POST /api/logsheets/checker/pending/:id/reject`. */
export interface RejectRequest {
  readonly reason: RejectionReason;
  readonly remarks: string;
}

/** A row in `GET /api/logsheets/checker/history`. */
export interface HistoryItem extends PendingItem {
  readonly status: 'Checked' | 'Rejected';
  readonly actedAt: string | null;
}

/** Request body for `POST /api/logsheets/checker/pending/bulk-approve`. */
export interface BulkApproveRequest {
  readonly ids: readonly number[];
  readonly remarks?: string;
}

/** Per-entry result row in bulk approve response. */
export interface BulkApproveItemResult {
  readonly id: number;
  readonly success: boolean;
  readonly error: string | null;
}

/** Aggregate result for bulk approve. */
export interface BulkApproveResult {
  readonly results: readonly BulkApproveItemResult[];
  readonly succeededCount: number;
  readonly failedCount: number;
}

/** Query parameters for `GET /api/logsheets/checker/pending`. */
export interface PendingListQuery {
  readonly siteId?: number;
  readonly machineId?: number;
  readonly fromDate?: string;
  readonly toDate?: string;
  readonly page?: number;
  readonly pageSize?: number;
  /** Phase 26 — when set, overrides the default `Submitted` queue filter so
   *  the checker can flip to Approved / Rejected entries from the same screen. */
  readonly status?: LogsheetStatus;
}

/** Query parameters for `GET /api/logsheets/checker/history`. */
export interface HistoryListQuery {
  readonly status?: 'Checked' | 'Rejected';
  readonly siteId?: number;
  readonly fromDate?: string;
  readonly toDate?: string;
  readonly page?: number;
  readonly pageSize?: number;
}
